import type { StackProps } from "@chakra-ui/react"
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Checkbox,
  Skeleton,
  Spacer,
  Stack,
  Text,
} from "@chakra-ui/react"
import type { FC } from "react"

import CardContainer from "common/components/CardContainer"
import type { OnchainVote, Vote } from "query/graphql"
import {
  useProposalPendingVotesQuery,
  useProposalVotesCastQuery,
  VoteType,
} from "query/graphql"
import { pluralize } from "common/helpers/string"
import { isMultiChainOrganization } from "organization/helpers/organization"
import { useOrganization } from "organization/providers/OrganizationProvider"
import { useProposal } from "proposal/providers/ProposalProvider"
import { useAddress } from "web3/hooks/useAddress"

type Props = {
  proposalId: string
}
const ProposalPendingVotes: FC<Props & StackProps> = ({
  proposalId,
  ...stackProps
}) => {
  const { isIncludePendingVotes, setIsIncludePendingVotes } = useProposal()
  const address = useAddress()

  const { organization } = useOrganization()
  const isMultiChain = organization
    ? isMultiChainOrganization(organization)
    : false

  const { data: votesData, isLoading: isVotesLoading } =
    useProposalVotesCastQuery(
      {
        input: {
          id: proposalId,
        },
      },
      { enabled: isMultiChain },
    )

  const { data: accountVotes } = useProposalPendingVotesQuery(
    {
      input: {
        filters: {
          proposalId,
          voter: address,
          includePendingVotes: true,
        },
      },
    },
    { enabled: isMultiChain && Boolean(address) },
  )

  if (!isMultiChain) return null

  if (isVotesLoading) {
    return (
      <CardContainer isTableView>
        <Skeleton h="30px" mt={4} mx={2} {...stackProps} />
      </CardContainer>
    )
  }

  // Calculate all pending votes count (number)
  const allPendingVotesCount =
    votesData &&
    votesData.proposal &&
    votesData.proposal.voteStats &&
    votesData.proposal.voteStats.length > 0
      ? votesData.proposal.voteStats.reduce((acc, { type, votersCount }) => {
          // TODO(@nicolas): Temporary fix since proposal.voteStats.type has a different value than VoteType
          // For example: "pending_for" vs "pendingfor" (from VoteType)
          if (
            type === VoteType.Pendingabstain ||
            type.replace("_", "") === VoteType.Pendingabstain ||
            type === VoteType.Pendingagainst ||
            type.replace("_", "") === VoteType.Pendingagainst ||
            type === VoteType.Pendingfor ||
            type.replace("_", "") === VoteType.Pendingfor
          )
            return acc + Number(votersCount)

          return acc
        }, 0)
      : 0

  // Calculate account pending votes (BigNumber)
  const accountPendingVotes = accountVotes?.votes?.nodes as Vote[]
  const accountPendingVotesCount =
    accountPendingVotes && accountPendingVotes.length > 0
      ? accountPendingVotes.filter(
          (vote): vote is OnchainVote => "chainId" in vote && !vote.isBridged,
        ).length
      : 0

  const hasPendingVotes = allPendingVotesCount > 0

  return (
    <CardContainer isTableView>
      <Stack pt={4}>
        <Box px={3}>
          <Checkbox
            colorScheme="purple"
            disabled={!hasPendingVotes}
            isChecked={isIncludePendingVotes}
            onChange={(e) => {
              e.stopPropagation()

              setIsIncludePendingVotes(!isIncludePendingVotes)
            }}
          >
            <Text color="gray.600" fontSize="sm" fontWeight="medium">
              Include pending votes in totals
            </Text>
          </Checkbox>
        </Box>

        <Accordion allowToggle borderWidth="0" mt={1} mx={2}>
          <AccordionItem border="none" borderRadius="md" borderWidth="0" px={0}>
            <AccordionButton
              _hover={{
                bgColor: hasPendingVotes ? "yellow.100" : "gray.100",
              }}
              bgColor={hasPendingVotes ? "yellow.100" : "gray.100"}
              borderRadius="md"
              pl={3}
              pr={2}
            >
              <Box
                color={hasPendingVotes ? "yellow.900" : "gray.900"}
                flex="1"
                fontSize="sm"
                fontWeight="medium"
                textAlign="left"
              >
                {allPendingVotesCount}{" "}
                {pluralize(allPendingVotesCount, "vote is", "votes are")}{" "}
                pending
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel
              bgColor="white"
              borderWidth="0"
              mx={0}
              pb={0}
              px={2}
            >
              <Stack direction="column">
                <Stack
                  color="gray.600"
                  direction="row"
                  fontSize="sm"
                  fontWeight="medium"
                >
                  <Text>Your pending votes</Text>
                  <Spacer />
                  <Text>{accountPendingVotesCount}</Text>
                </Stack>

                <Stack
                  color="gray.600"
                  direction="row"
                  fontSize="sm"
                  fontWeight="medium"
                >
                  <Text>All pending votes</Text>
                  <Spacer />
                  <Text>{allPendingVotesCount}</Text>
                </Stack>

                <Text
                  color="gray.600"
                  fontSize="sm"
                  fontWeight="regular"
                  mt={3}
                >
                  Votes automatically transferred every ~15 min
                </Text>
              </Stack>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      </Stack>
    </CardContainer>
  )
}

export default ProposalPendingVotes
